import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../../router";
import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "../../../../components/AuthProvider";
import { createRouteError } from "../../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../../components/RouteSkeleton";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { Separator } from "../../../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { toast } from "../../../../components/ui/sonner";
import {
  AlertTriangle,
  Settings as SettingsIcon,
  Calendar,
  Zap,
  FileText,
  Save,
  Loader2,
  Focus,
  Globe,
  Key,
  Bell,
  Shield,
  Webhook,
  Layout,
  Phone,
  Mail,
  MapPin,
  Building2,
  Hash,
  Link,
} from "lucide-react";

export const Route = createFileRoute("/_admin/admin/settings/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
    // Session validated — settings use local state (localStorage).
    // TODO: Replace with real settings API fetch in future phase.
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Settings"),
  component: SettingsPage,
});

// ── Constants ─────────────────────────────────────────────────────────────

const SETTINGS_STORAGE_KEY = "flowstate-admin-general-settings";

interface GeneralSettings {
  requireEmailVerification: boolean;
  allowUserRegistration: boolean;
  enableFocusMode: boolean;
  enableNotificationEmails: boolean;
}

const DEFAULT_SETTINGS: GeneralSettings = {
  requireEmailVerification: true,
  allowUserRegistration: true,
  enableFocusMode: false,
  enableNotificationEmails: true,
};

// ── Landing Page Settings ─────────────────────────────────────────────────

interface LandingPageSettings {
  phone: string;
  phone_display: string;
  email: string;
  address_line1: string;
  address_line2: string;
  address_country: string;
  company_name: string;
  company_legal_name: string;
  abn: string;
  facebook_url: string;
  linkedin_url: string;
  twitter_url: string;
  instagram_url: string;
}

const DEFAULT_LANDING_SETTINGS: LandingPageSettings = {
  phone: "0493652662",
  phone_display: "0493 652 662",
  email: "hello@flowstateautomate.ai",
  address_line1: "440 Collins Street",
  address_line2: "Melbourne, VIC 3000",
  address_country: "Australia",
  company_name: "FlowState Automate",
  company_legal_name: "FlowState Automate Pty Ltd",
  abn: "",
  facebook_url: "",
  linkedin_url: "",
  twitter_url: "",
  instagram_url: "",
};

async function fetchLandingSettings(supabase: SupabaseClient): Promise<LandingPageSettings> {
  const { data, error } = await supabase
    .from("landing_settings")
    .select("key, value");

  if (error || !data || data.length === 0) {
    return DEFAULT_LANDING_SETTINGS;
  }

  const rows = data as Array<{ key: string; value: string }>;
  const map = new Map(rows.map((row) => [row.key, row.value]));

  return {
    phone: map.get("phone") ?? DEFAULT_LANDING_SETTINGS.phone,
    phone_display: map.get("phone_display") ?? DEFAULT_LANDING_SETTINGS.phone_display,
    email: map.get("email") ?? DEFAULT_LANDING_SETTINGS.email,
    address_line1: map.get("address_line1") ?? DEFAULT_LANDING_SETTINGS.address_line1,
    address_line2: map.get("address_line2") ?? DEFAULT_LANDING_SETTINGS.address_line2,
    address_country: map.get("address_country") ?? DEFAULT_LANDING_SETTINGS.address_country,
    company_name: map.get("company_name") ?? DEFAULT_LANDING_SETTINGS.company_name,
    company_legal_name: map.get("company_legal_name") ?? DEFAULT_LANDING_SETTINGS.company_legal_name,
    abn: map.get("abn") ?? DEFAULT_LANDING_SETTINGS.abn,
    facebook_url: map.get("facebook_url") ?? DEFAULT_LANDING_SETTINGS.facebook_url,
    linkedin_url: map.get("linkedin_url") ?? DEFAULT_LANDING_SETTINGS.linkedin_url,
    twitter_url: map.get("twitter_url") ?? DEFAULT_LANDING_SETTINGS.twitter_url,
    instagram_url: map.get("instagram_url") ?? DEFAULT_LANDING_SETTINGS.instagram_url,
  };
}

const loadSettings = (): GeneralSettings => {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const saved = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (saved) {
      return { ...DEFAULT_SETTINGS, ...(JSON.parse(saved) as Partial<GeneralSettings>) };
    }
  } catch {
    // ignore parse errors
  }
  return DEFAULT_SETTINGS;
};

// ── Main Page ─────────────────────────────────────────────────────────────

function SettingsPage() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return <AuthenticatedSettings />;
}

// ── Authenticated sub-component ───────────────────────────────────────────

function AuthenticatedSettings() {
  const { supabase } = Route.useRouteContext();
  const [activeTab, setActiveTab] = useState("general");
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<GeneralSettings>(loadSettings);
  const savedSettingsRef = useRef<GeneralSettings>(loadSettings());
  const queryClient = useQueryClient();

  const isDirty =
    JSON.stringify(settings) !== JSON.stringify(savedSettingsRef.current);

  const handleSettingChange = (key: keyof GeneralSettings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSettings = useCallback(async () => {
    setIsSaving(true);
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      savedSettingsRef.current = { ...settings };
      toast.success("Settings saved successfully");
    } catch {
      toast.error("Failed to save settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  // ── Landing Page Settings State & Queries ──────────────────────────────

  const {
    data: landingSettings = DEFAULT_LANDING_SETTINGS,
    isLoading: landingLoading,
  } = useQuery({
    queryKey: ["landing-settings"],
    queryFn: () => {
      if (!supabase) throw new Error("Supabase client not available");
      return fetchLandingSettings(supabase);
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!supabase,
  });

  const [landingForm, setLandingForm] = useState<LandingPageSettings>(DEFAULT_LANDING_SETTINGS);
  const landingSavedRef = useRef<LandingPageSettings>(DEFAULT_LANDING_SETTINGS);
  const [isSavingLanding, setIsSavingLanding] = useState(false);

  useEffect(() => {
    if (!landingLoading) {
      setLandingForm(landingSettings);
      landingSavedRef.current = landingSettings;
    }
  }, [landingLoading, landingSettings]);

  const isLandingDirty =
    JSON.stringify(landingForm) !== JSON.stringify(landingSavedRef.current);

  const updateLandingField = (key: keyof LandingPageSettings, value: string) => {
    setLandingForm((prev) => ({ ...prev, [key]: value }));
  };

  const landingMutation = useMutation({
    mutationFn: async (values: LandingPageSettings) => {
      if (!supabase) throw new Error("Supabase client not available");
      const rows = (Object.entries(values) as Array<[string, string]>).map(([key, value]) => ({
        key,
        value,
        category:
          key.startsWith("address") || key === "phone" || key === "phone_display" || key === "email"
            ? "contact"
            : key.startsWith("company") || key === "abn"
            ? "company"
            : "social",
      }));
      const { error } = await supabase.from("landing_settings").upsert(rows, {
        onConflict: "key",
      });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      landingSavedRef.current = { ...variables };
      queryClient.setQueryData(["landing-settings"], variables);
      toast.success("Landing page settings saved");
    },
    onError: () => {
      toast.error("Failed to save landing page settings");
    },
  });

  const handleSaveLandingSettings = useCallback(async () => {
    setIsSavingLanding(true);
    try {
      await landingMutation.mutateAsync(landingForm);
    } finally {
      setIsSavingLanding(false);
    }
  }, [landingForm, landingMutation]);

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold">Admin Settings</h1>
        <p className="text-muted-foreground mt-2">
          Configure system settings, integrations, and diagnostic tools.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="landing" className="flex items-center gap-2">
            <Layout className="w-4 h-4" />
            Landing
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="api" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            API
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          {/* Focus Mode */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Focus className="h-5 w-5" />
                Focus Mode
              </CardTitle>
              <CardDescription>
                Simplify the sidebar to reduce cognitive load. Shows only
                essential items and groups everything else under a collapsible
                &quot;More&quot; section.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="focus-mode">Enable Focus Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Recommended for a cleaner, distraction-free experience
                  </p>
                </div>
                <Switch
                  id="focus-mode"
                  checked={settings.enableFocusMode}
                  onCheckedChange={() => handleSettingChange("enableFocusMode")}
                />
              </div>
            </CardContent>
          </Card>

          {/* User Authentication */}
          <Card>
            <CardHeader>
              <CardTitle>User Authentication Settings</CardTitle>
              <CardDescription>
                Configure how users authenticate and register on the platform.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-verification">
                    Require Email Verification
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Users must verify their email before accessing the platform
                  </p>
                </div>
                <Switch
                  id="email-verification"
                  checked={settings.requireEmailVerification}
                  onCheckedChange={() =>
                    handleSettingChange("requireEmailVerification")
                  }
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="allow-registration">
                    Allow User Registration
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Enable or disable new user registrations
                  </p>
                </div>
                <Switch
                  id="allow-registration"
                  checked={settings.allowUserRegistration}
                  onCheckedChange={() =>
                    handleSettingChange("allowUserRegistration")
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <p className="text-sm text-muted-foreground">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Changes to authentication settings may require service restart
              </p>
            </CardFooter>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3 items-center">
            {isDirty && (
              <span className="text-sm text-amber-500">Unsaved changes</span>
            )}
            <Button
              size="lg"
              onClick={handleSaveSettings}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save General Settings
            </Button>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Connected Services
              </CardTitle>
              <CardDescription>
                Manage external service integrations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <IntegrationRow
                name="Google Calendar"
                icon={<Calendar className="h-5 w-5 text-blue-500" />}
                description="Sync events and bookings with Google Calendar"
                connected={false}
              />
              <Separator />
              <IntegrationRow
                name="Xero Accounting"
                icon={<FileText className="h-5 w-5 text-sky-500" />}
                description="Sync invoices, contacts, and financial data"
                connected={false}
              />
              <Separator />
              <IntegrationRow
                name="Outbound Webhooks"
                icon={<Webhook className="h-5 w-5 text-orange-500" />}
                description="Configure activity webhooks for Zapier, Make, or custom endpoints"
                connected={false}
                href="/workflows"
              />
              <Separator />
              <IntegrationRow
                name="Slack"
                icon={<Zap className="h-5 w-5 text-purple-500" />}
                description="Receive notifications and updates in Slack"
                connected={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>
                Configure how and when you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="email-notifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive email notifications for important events
                  </p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={settings.enableNotificationEmails}
                  onCheckedChange={() =>
                    handleSettingChange("enableNotificationEmails")
                  }
                />
              </div>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Notification Categories</h4>
                <div className="grid gap-3">
                  {[
                    { label: "Task assignments", description: "When you are assigned a new task" },
                    { label: "Project updates", description: "Status changes on your projects" },
                    { label: "Client activity", description: "New client interactions and updates" },
                    { label: "Team mentions", description: "When someone mentions you in comments" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-1">
                      <div>
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Landing Page Tab */}
        <TabsContent value="landing" className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contact Information
              </CardTitle>
              <CardDescription>
                Phone, email, and address displayed on the public landing page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lp-phone">Phone Number</Label>
                  <Input
                    id="lp-phone"
                    value={landingForm.phone}
                    onChange={(e) => updateLandingField("phone", e.target.value)}
                    placeholder="0493652662"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lp-phone-display">Display Phone</Label>
                  <Input
                    id="lp-phone-display"
                    value={landingForm.phone_display}
                    onChange={(e) => updateLandingField("phone_display", e.target.value)}
                    placeholder="0493 652 662"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lp-email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="lp-email"
                  type="email"
                  value={landingForm.email}
                  onChange={(e) => updateLandingField("email", e.target.value)}
                  placeholder="hello@flowstateautomate.ai"
                  className="mt-1"
                />
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lp-address1">Address Line 1</Label>
                  <Input
                    id="lp-address1"
                    value={landingForm.address_line1}
                    onChange={(e) => updateLandingField("address_line1", e.target.value)}
                    placeholder="440 Collins Street"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lp-address2">Address Line 2</Label>
                  <Input
                    id="lp-address2"
                    value={landingForm.address_line2}
                    onChange={(e) => updateLandingField("address_line2", e.target.value)}
                    placeholder="Melbourne, VIC 3000"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lp-country" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Country
                </Label>
                <Input
                  id="lp-country"
                  value={landingForm.address_country}
                  onChange={(e) => updateLandingField("address_country", e.target.value)}
                  placeholder="Australia"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
              <CardDescription>
                Company name and legal details shown on the landing page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lp-company">Company Name</Label>
                  <Input
                    id="lp-company"
                    value={landingForm.company_name}
                    onChange={(e) => updateLandingField("company_name", e.target.value)}
                    placeholder="FlowState Automate"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lp-legal">Legal Name</Label>
                  <Input
                    id="lp-legal"
                    value={landingForm.company_legal_name}
                    onChange={(e) => updateLandingField("company_legal_name", e.target.value)}
                    placeholder="FlowState Automate Pty Ltd"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lp-abn" className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  ABN
                </Label>
                <Input
                  id="lp-abn"
                  value={landingForm.abn}
                  onChange={(e) => updateLandingField("abn", e.target.value)}
                  placeholder=""
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Social Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="h-5 w-5" />
                Social Links
              </CardTitle>
              <CardDescription>
                URLs for social media profiles linked from the landing page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lp-facebook">Facebook URL</Label>
                  <Input
                    id="lp-facebook"
                    value={landingForm.facebook_url}
                    onChange={(e) => updateLandingField("facebook_url", e.target.value)}
                    placeholder="https://facebook.com/..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lp-linkedin">LinkedIn URL</Label>
                  <Input
                    id="lp-linkedin"
                    value={landingForm.linkedin_url}
                    onChange={(e) => updateLandingField("linkedin_url", e.target.value)}
                    placeholder="https://linkedin.com/company/..."
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lp-twitter">Twitter URL</Label>
                  <Input
                    id="lp-twitter"
                    value={landingForm.twitter_url}
                    onChange={(e) => updateLandingField("twitter_url", e.target.value)}
                    placeholder="https://twitter.com/..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lp-instagram">Instagram URL</Label>
                  <Input
                    id="lp-instagram"
                    value={landingForm.instagram_url}
                    onChange={(e) => updateLandingField("instagram_url", e.target.value)}
                    placeholder="https://instagram.com/..."
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3 items-center">
            {isLandingDirty && (
              <span className="text-sm text-amber-500">Unsaved changes</span>
            )}
            <Button
              size="lg"
              onClick={handleSaveLandingSettings}
              disabled={!isLandingDirty || isSavingLanding}
            >
              {isSavingLanding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Landing Settings
            </Button>
          </div>
        </TabsContent>

        {/* API Tab */}
        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Configuration
              </CardTitle>
              <CardDescription>
                Manage API keys and access tokens
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="api-url">API Base URL</Label>
                <Input
                  id="api-url"
                  value={typeof window !== "undefined" ? window.location.origin + "/api" : "/api"}
                  readOnly
                  className="bg-muted mt-1"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This is the base URL for all API requests
                </p>
              </div>
              <Separator />
              <div>
                <Label>API Keys</Label>
                <p className="text-sm text-muted-foreground mt-1 mb-3">
                  API keys are managed through the Supabase dashboard. Each key
                  provides different levels of access to the platform API.
                </p>
                <div className="rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="h-4 w-4" />
                    API keys are securely stored and never exposed in the client
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}

// ── Integration Row Component ─────────────────────────────────────────────

function IntegrationRow({
  name,
  icon,
  description,
  connected,
  href,
}: {
  name: string;
  icon: React.ReactNode;
  description: string;
  connected: boolean;
  href?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <p className="text-sm font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {href ? (
        <Button variant="outline" size="sm" asChild>
          <a href={href}>Manage</a>
        </Button>
      ) : (
        <Button variant={connected ? "outline" : "default"} size="sm">
          {connected ? "Configure" : "Connect"}
        </Button>
      )}
    </div>
  );
}
